/* eslint-disable @typescript-eslint/no-magic-numbers */
import React from 'react';
import { formatTime, getOrCreateAudio } from '../../utils';
import { AudioPlayerProps, AudioPlayerRef } from './model';
import { View } from './view';
export type { AudioPlayerProps, AudioPlayerRef } from './model';

export const AudioPlayer = React.forwardRef<AudioPlayerRef, AudioPlayerProps>(
  ({ source, onClick, duration, size = 'normal', autoPlay = false, sx, frozenDuration }, ref) => {
    const [isPaused, setIsPaused] = React.useState(!autoPlay);
    const [progress, setProgress] = React.useState(0);
    const [totalDuration, setTotalDuration] = React.useState<number | null>(duration ?? null);
    const [currentTime, setCurrentTime] = React.useState(0);

    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const onClickRef = React.useRef(onClick);

    React.useEffect(() => {
      onClickRef.current = onClick;
    }, [onClick]);

    const handleEnded = React.useCallback(() => {
      setIsPaused(true);
      setProgress(0);
      setCurrentTime(0);
      onClickRef.current?.();
    }, []);

    const handleTimeUpdate = () => {
      const audio = audioRef.current;
      if (audio && audio.duration && Number.isFinite(audio.duration) && audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setCurrentTime(audio.currentTime);
      }
    };

    const handlePlayButtonClick = () => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }

      if (isPaused) {
        if (audio.ended || (Number.isFinite(audio.duration) && audio.duration > 0 && audio.currentTime >= audio.duration)) {
          audio.currentTime = 0;
        }
        audio.play();
      } else {
        audio.pause();
        onClickRef.current?.();
      }
      setIsPaused(!isPaused);
    };

    const play = React.useCallback(() => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }

      if (audio.ended || (Number.isFinite(audio.duration) && audio.duration > 0 && audio.currentTime >= audio.duration)) {
        audio.currentTime = 0;
      }

      audio.play().catch(console.error);
      setIsPaused(false);
    }, []);

    const pause = React.useCallback(() => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }

      audio.pause();
      audio.currentTime = 0;
      setIsPaused(true);
      setProgress(0);
      setCurrentTime(0);
    }, []);

    const setTime = React.useCallback(
      (time: number) => {
        const audio = audioRef.current;
        if (!audio) {
          return;
        }

        const fallbackDuration = duration ?? totalDuration ?? 0;
        const maxDuration =
          Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : Number.isFinite(fallbackDuration) && fallbackDuration > 0
              ? fallbackDuration
              : 0;

        const clampedTime = maxDuration > 0 ? Math.min(Math.max(0, time), maxDuration) : Math.max(0, time);

        audio.currentTime = clampedTime;
        setCurrentTime(clampedTime);
        setProgress(maxDuration > 0 ? (clampedTime / maxDuration) * 100 : 0);
      },
      [duration, totalDuration],
    );

    React.useImperativeHandle(ref, () => ({ play, pause, setTime }), [play, pause, setTime]);

    React.useEffect(() => {
      const audio = getOrCreateAudio(source);
      audioRef.current = audio;

      const updateDuration = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setTotalDuration(audio.duration);
        }
      };

      const handleLoadedData = () => {
        updateDuration();
        if (autoPlay) {
          if (audio.paused) {
            audio
              .play()
              .then(() => setIsPaused(false))
              .catch(console.error);
          }
        }
      };

      updateDuration();

      audio.addEventListener('loadedmetadata', updateDuration);
      audio.addEventListener('loadeddata', handleLoadedData);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);

      if (audio.readyState < 1) {
        audio.load();
      }

      if (autoPlay) {
        audio
          .play()
          .then(() => {
            setIsPaused(false);
          })
          .catch(console.error);
      }

      return () => {
        audio.pause();
        audio.removeEventListener('loadedmetadata', updateDuration);
        audio.removeEventListener('loadeddata', handleLoadedData);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audioRef.current = null;
      };
    }, [source, autoPlay, handleEnded]);

    const validatedDuration = ((): number => {
      const durationValue = duration ?? totalDuration ?? 0;
      return Number.isFinite(durationValue) && durationValue >= 0 ? durationValue : 0;
    })();

    const remainingTime = Math.max(0, validatedDuration - (frozenDuration ? 0 : currentTime));

    const viewProps = {
      sx,
      size,
      onPlay: handlePlayButtonClick,
      audioDuration: formatTime(remainingTime),
      progress,
      isPaused,
    };

    return <View duration={totalDuration ?? 0} {...viewProps} />;
  },
);

AudioPlayer.displayName = 'AudioPlayer';

export * from './components/track';
